import instance, { unauthenticatedInstance } from "./httpClient";

// Session and profile

export function login(id, password) {
  return instance
    .post("/login", { email: id, password: password })
    .then((response) => {
      return response.data;
    });
}

export function exchangeClassroomTransitionHandoff(code) {
  if (typeof code !== "string" || code.trim() === "") {
    return Promise.reject(new Error("A classroom transition hand-off code is required."));
  }

  return unauthenticatedInstance
    .post("/classroom-transition-handoffs/exchange", { code })
    .then((response) => response.data);
}

export function whoami() {
  return instance.get("/whoAmI").then((response) => {
    return response;
  });
}

const getClassroomTransitionPath = ({ schoolId, targetAcademicYear }, suffix = "") =>
  `/schools/${encodeURIComponent(schoolId)}/classroom-transitions/${encodeURIComponent(targetAcademicYear)}${suffix}`;

const getClassroomTransitionResponseData = (response) => {
  const data = response.data;
  const status = Number(data?.statusCode);
  if (Number.isInteger(status) && status >= 400) {
    const error = new Error(data.message || "Classroom Transition request failed.");
    error.response = { ...response, data, status };
    throw error;
  }
  return data;
};

export function getClassroomTransitionPreflight({
  schoolId,
  targetAcademicYear,
  source,
  launchId
} = {}) {
  const params = Object.fromEntries(
    Object.entries({ schoolId, targetAcademicYear, source, launchId }).filter(
      ([, value]) => value !== undefined && value !== null && String(value).trim() !== ""
    )
  );

  return instance
    .get("/teachers/me/classroom-transitions/preflight", { params })
    .then(getClassroomTransitionResponseData);
}

export function claimClassroomTransitionOwnership({
  schoolId,
  targetAcademicYear
}) {
  return instance
    .post(
      getClassroomTransitionPath(
        { schoolId, targetAcademicYear },
        "/ownership/claim"
      )
    )
    .then(getClassroomTransitionResponseData);
}

export function saveClassroomTransitionScope({
  schoolId,
  targetAcademicYear,
  expectedScopeVersion,
  progressionPoints
}) {
  return instance
    .put(
      getClassroomTransitionPath({ schoolId, targetAcademicYear }, "/scope"),
      { expectedScopeVersion, progressionPoints }
    )
    .then(getClassroomTransitionResponseData);
}

export function getClassroomTransitionRoster({
  schoolId,
  targetAcademicYear,
  progressionPointId
}) {
  return instance
    .get(
      getClassroomTransitionPath(
        { schoolId, targetAcademicYear },
        `/progression-points/${encodeURIComponent(progressionPointId)}/roster`
      )
    )
    .then(getClassroomTransitionResponseData);
}

export function getClassroomTransitionReconciliation({
  schoolId,
  targetAcademicYear,
  progressionPointId
}) {
  return instance
    .get(
      getClassroomTransitionPath(
        { schoolId, targetAcademicYear },
        `/progression-points/${encodeURIComponent(progressionPointId)}/reconciliation`
      )
    )
    .then(getClassroomTransitionResponseData);
}

export function reconcileClassroomTransitionProgressionPoint({
  schoolId,
  targetAcademicYear,
  progressionPointId,
  idempotencyKey,
  ...payload
}) {
  return instance
    .post(
      getClassroomTransitionPath(
        { schoolId, targetAcademicYear },
        `/progression-points/${encodeURIComponent(progressionPointId)}/reconciliation`
      ),
      payload,
      { headers: { "Idempotency-Key": idempotencyKey } }
    )
    .then(getClassroomTransitionResponseData);
}

export function createClassroomTransitionDraft({
  schoolId,
  targetAcademicYear,
  ...payload
}) {
  return instance
    .post(
      getClassroomTransitionPath({ schoolId, targetAcademicYear }, "/classroom-drafts"),
      payload
    )
    .then(getClassroomTransitionResponseData);
}

export function updateClassroomTransitionDraft({
  schoolId,
  targetAcademicYear,
  draftId,
  ...payload
}) {
  return instance
    .put(
      getClassroomTransitionPath(
        { schoolId, targetAcademicYear },
        `/classroom-drafts/${encodeURIComponent(draftId)}`
      ),
      payload
    )
    .then(getClassroomTransitionResponseData);
}

export function discardClassroomTransitionDraft({
  schoolId,
  targetAcademicYear,
  draftId,
  version
}) {
  return instance
    .delete(
      getClassroomTransitionPath(
        { schoolId, targetAcademicYear },
        `/classroom-drafts/${encodeURIComponent(draftId)}`
      ),
      { data: { version } }
    )
    .then(getClassroomTransitionResponseData);
}

export function confirmClassroomTransitionDraft({
  schoolId,
  targetAcademicYear,
  draftId,
  idempotencyKey,
  ...payload
}) {
  return instance
    .post(
      getClassroomTransitionPath(
        { schoolId, targetAcademicYear },
        `/classroom-drafts/${encodeURIComponent(draftId)}/confirm`
      ),
      payload,
      { headers: { "Idempotency-Key": idempotencyKey } }
    )
    .then(getClassroomTransitionResponseData);
}

export function searchClassroomTransitionChildren({
  schoolId,
  targetAcademicYear,
  query,
  limit = 25
}) {
  return instance
    .get(
      getClassroomTransitionPath({ schoolId, targetAcademicYear }, "/children/search"),
      { params: { query, limit } }
    )
    .then(getClassroomTransitionResponseData);
}

export function getLastLogin(teacherid) {
  return instance
    .get(`/lastloggedin/${teacherid}`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error fetching last login:", error);
      throw error;
    });
}

export function postLastLogin(teacherid) {
  return instance
    .post("/lastloggedin", { teacherid })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error creating Logging Last Login:", error);
      throw error;
    });
}

export function getSchools() {
  return instance.get("/schools");
}

export function getSchoolRoles(schoolid) {
  return instance.get(`/schools/${schoolid}/roles`);
}

export function getCountryGrades(countryid) {
  return instance.get(`/grades/${countryid}`).then((response) => {
    return response.data;
  });
}

export function getCountryId(schoolId) {
  return instance
    .get(`/schools/${schoolId}/country-id`)
    .then((response) => {
      return response.data;
    });
}

export function updateTeacherProfile(schoolId, teacherId, data) {
  return instance
    .patch(`/schools/${schoolId}/teachers/${teacherId}`, data)
    .then((response) => {
      return response.data;
    });
}

// Classes, students, and access

export function getClasses() {
  return instance.get(`classes`).then((response) => {
    return response.data;
  });
}

export async function getClassesBySchoolId(schoolid) {
  try {
    const response = await instance.get(`/schools/${schoolid}/classes/csd`);
    return response.data;
  } catch (error) {
    console.error('Error fetching classes for school:', error);
    throw error;
  }
}

export async function getClassesByTeacherId(teacherid) {
  try {
    const response = await instance.get(`/teachers/${teacherid}/classes/csd`);
    return response.data;
  } catch (error) {
    console.error('Error fetching students for class:', error);
    throw error;
  }
}

export async function getStudentsByClassId(schoolid, classid) {
  try {
    
    
    

    const response = await instance.get(`/schools/${schoolid}/classes/${classid}/students/csd`);

    
    

    return response.data;
  } catch (error) {
    console.error('❌ [getStudentsByClassId] Error occurred');
    console.error('❌ [getStudentsByClassId] Error message:', error.message);
    console.error('❌ [getStudentsByClassId] Error status:', error.response?.status);
    console.error('❌ [getStudentsByClassId] Error data:', error.response?.data);
    throw error;
  }
}

export function saveClassroomReadinessReview(schoolid, classid, payload, idempotencyKey) {
  return instance
    .post(`/schools/${schoolid}/classes/${classid}/readiness/csd`, payload, {
      headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined
    })
    .then((response) => response.data);
}

export function searchTeacherStudents(query, limit = 25) {
  return instance
    .get("/teachers/me/students/search/csd", {
      params: {
        query,
        limit,
      },
    })
    .then((response) => response.data);
}

export function getIndividualStudentResults(studentid) {
  return instance
    .get(`students/${studentid}/assessments/results`)
    .then((response) => {
      
      return response.data;
    });
}

export function addStudents(classid, name) {
  if (name?.length > 0) {
    return instance
      .post(`classes/${classid}/students`, name)
      .then((response) => {
        return response.data;
      });
  } else {
    return null;
  }
}

export function updateStudent(studentId, data) {
  return instance.patch(`/students/${studentId}`, data).then((response) => {
    return response.data;
  });
}

export async function batchDeleteStudents(data) {
  try {
    const response = await instance.delete('/students/batch', {
      data: {
        studentIds: data.studentIds,
        deletionReason: data.deletionReason,
        deletedByTeacherId: data.deletedByTeacherId,
        deletedByTeacherName: data.deletedByTeacherName
      }
    });
    return response.data; // Returns the detailed response with counts and errors
  } catch (error) {
    console.error('Error batch deleting students:', error);
    throw error;
  }
}

export function bulkUpdateStudentClassId({ studentIds, classId }) {
  
  return instance.patch("/students/bulk/class-id", {
    studentIds: studentIds,
    classId: classId
  }).then((response) => {
    return response.data;
  });
}

export function createClass(
  name,
  grade,
  grade1,
  grade2,
  grade3,
  grade4,
  trialClass,
  specialEdClass, // Should be boolean
  classOwner
) {
  return instance
    .post(`/classes`, {
      name: name,
      grade_level: grade,
      grade_level1: grade1,
      grade_level2: grade2,
      grade_level3: grade3,
      grade_level4: grade4,
      trial_class: trialClass,
      specialed_class: specialEdClass, // Ensure this is a boolean
      classOwnerId: classOwner,
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error creating class:", error.message);
      if (error.response && error.response.data) {
        console.error("Error details:", error.response.data);
      }
      throw error; // Rethrow the error to be handled in the calling code
    });
}

export function editClass(classid, nameOfClass) {
  return instance
    .patch(`classes/${classid}`, {
      name: `${nameOfClass}`,
    })
    .then((response) => {
      return response.data;
    });
}

export async function softDeleteClass(schoolid, data) {
  try {
    
    
    
    




    const response = await instance.delete(`/schools/${schoolid}/classes/${data.classid}/csd`, {
      data: {
        deletionReason: data.deletionReason,
        teacherId: data.teacherId
      }
    });

    
    

    return response;
  } catch (error) {
    console.error('❌ [softDeleteClass] Error occurred');
    console.error('❌ [softDeleteClass] Error message:', error.message);
    console.error('❌ [softDeleteClass] Error status:', error.response?.status);
    console.error('❌ [softDeleteClass] Error data:', error.response?.data);
    throw error;
  }
}

export function mergeClasses(schoolid, payload, idempotencyKey) {
  return instance
    .post(`/schools/${schoolid}/classes/merge`, payload, {
      headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
    })
    .then((response) => response.data);
}

export function createClassAccessRequest(schoolId, classId, { targetTeacherId, note } = {}) {
  const body = {};

  if (targetTeacherId) body.targetTeacherId = targetTeacherId;
  if (note) body.note = note;

  return instance
    .post(`/schools/${schoolId}/classes/${classId}/access-requests`, body)
    .then((response) => response.data)
    .catch((error) => {
      console.error("Error creating class access request:", error);
      throw error;
    });
}

export function getSharedTeachersByClassId(classId) {
  return instance
    .get(`/classes/${classId}/shared-teachers`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error('Error fetching shared teachers:', error);
      throw error;
    });
}

export function shareClass(classid, teacherid, isSpecialCondition = false) {
  return instance
    .patch(`classes/${classid}/share`, {
      op: "add",
      teacherid: `${teacherid}`,
      isSpecialCondition,
    })
    .then((response) => {
      return response.data;
    });
}

export function removeFromSharedClass(classId, teacherId) {
  

  return instance.delete(`/classes/${classId}/teachers/${teacherId}/shared`)
    .then(response => {
      
      return response;
    })
    .catch(error => {
      console.error(`Error removing teacher ${teacherId} from shared class ${classId}.`, error);
      throw error;
    });
}

export function removeOrReplaceClassOwner(classId, newClassOwnerId) {
  

  return instance.patch(`/classes/${classId}/owner`, { newClassOwnerId })
    .then(response => {
      if (newClassOwnerId) {
        
      } else {
        
      }
      return response;
    })
    .catch(error => {
      console.error(`Error ${newClassOwnerId ? 'replacing' : 'removing'} class owner for class ${classId}.`, error);
      throw error;
    });
}

export function replaceClassCreator(classId, newTeacherId) {
  

  return instance.patch(`/classes/${classId}/creator`, { newTeacherId })
    .then(response => {
      
      return response;
    })
    .catch(error => {
      console.error(`Error replacing class creator for class ${classId} with teacher ${newTeacherId}.`, error);
      throw error;
    });
}

// Results and task resets

export async function getAssessmentsByStudentId(schoolid, studentid) {
  try {
    
    
    

    const response = await instance.get(`/schools/${schoolid}/students/${studentid}/assessments/csd`);

    
    

    return response.data;
  } catch (error) {
    console.error('❌ [getAssessmentsByStudentId] Error occurred');
    console.error('❌ [getAssessmentsByStudentId] Error message:', error.message);
    console.error('❌ [getAssessmentsByStudentId] Error status:', error.response?.status);
    console.error('❌ [getAssessmentsByStudentId] Error data:', error.response?.data);
    throw error;
  }
}

export function getStudentDuplicateResults(studentId) {
  return instance
    .get(`/students/${studentId}/duplicate-results`)
    .then((response) => response.data);
}

export async function swapAssessmentStudents(schoolid, firstAssessmentInstanceId, secondAssessmentInstanceId, reason = "") {
  try {
    
    
    
    

    const response = await instance.patch(`/schools/${schoolid}/assessments/swap/csd`, {
      firstAssessmentInstanceId,
      secondAssessmentInstanceId,
      reason,
    });

    
    

    return response;
  } catch (error) {
    console.error('❌ [swapAssessmentStudents] Error occurred');
    console.error('❌ [swapAssessmentStudents] Error message:', error.message);
    console.error('❌ [swapAssessmentStudents] Error status:', error.response?.status);
    console.error('❌ [swapAssessmentStudents] Error data:', error.response?.data);
    throw error;
  }
}

export async function transferAssessmentToStudent(schoolid, assessmentInstanceId, targetStudentId, reason = "") {
  try {
    
    
    
    

    const response = await instance.post(`/schools/${schoolid}/assessments/transfer/teacher`, {
      assessmentInstanceId,
      targetStudentId,
      reason,
    });

    
    

    return response.data;
  } catch (error) {
    console.error('❌ [transferAssessmentToStudent] Error occurred');
    console.error('❌ [transferAssessmentToStudent] Error message:', error.message);
    console.error('❌ [transferAssessmentToStudent] Error status:', error.response?.status);
    console.error('❌ [transferAssessmentToStudent] Error data:', error.response?.data);
    throw error;
  }
}

export function unassignAssessmentBatch(assessmentInstanceIds) {
  

  return instance
    .delete('/assessment-instances/batch', {
      data: { ids: assessmentInstanceIds }
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error('Error in batch unassign:', error);
      throw error;
    });
}

export function getStudentDeletedAnswers(studentid) {
  return instance
    .get(`/students/${studentid}/deleted-answers`)
    .then((response) => response.data);
}

export function keepNewerDeletedAnswers(studentid, payload) {
  return instance
    .post(`/students/${studentid}/deleted-answers/keep-newer`, payload)
    .then((response) => response.data);
}

export function restoreAnswer(id, auditDetails = {}) {
  return instance
    .post(`/deleted/answers/${id}/restore`, auditDetails)
    .then((response) => {
      return response.data;
    });
}

export function batchRestoreAnswers(schoolid, deletedAnswerIds, auditDetails = {}) {
  return instance
    .post(`/deleted/answers/school/${schoolid}/batch-restore`, {
      deletedAnswerIds,
      ...auditDetails
    })
    .then((response) => {
      return response.data;
    });
}

export function getAnswerDeletionReasonOptions() {
  return instance
    .get("answer-deletion-reasons")
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error fetching AnswerDeletionReason options:", error);
      throw error;
    });
}

export function deleteAnswers(deletionData) {
  return instance
    .delete("/answers", {
      data: {
        answerIds: deletionData.answerIds,
        teacherId: deletionData.teacherId,
        deletionReasonId: deletionData.deletionReasonId,
        deletionReason: deletionData.deletionReason
      }
    })
    .then((response) => {
      
      if (response.status === 204) {
        return true;
      } else {
        console.error("Unexpected response status:", response.status);
        return false;
      }
    })
    .catch((error) => {
      if (error.response) {
        console.error("Error deleting answers:", error.message);
        console.error("Status code:", error.response.status);
        console.error("Error data:", error.response.data);
      } else if (error.request) {
        console.error("No response received when deleting answers:", error.request);
      } else {
        console.error("Error setting up request to delete answers:", error.message);
      }
      return false;
    });
}

// Screeners and login bypass

export function getAssessmentScreener(options = {}) {
  // Build query parameters object, only including defined values
  const params = {};

  if (options.countryid !== undefined) {
    params.countryid = options.countryid;
  }

  if (options.grade_level !== undefined) {
    params.grade_level = options.grade_level;
  }

  if (options.rti !== undefined) {
    params.rti = options.rti;
  }

  if (options.active !== undefined) {
    params.active = options.active;
  }

  if (options.standard !== undefined) {
    params.standard = options.standard;
  }

  if (options.task !== undefined) {
    params.task = options.task;
  }

  return instance
    .get("/assessments/screener", { params })
    .then((response) => {
      return response.data;
    });
}

export function assignScreenerToClass(schoolid, classid, assessmentId) {
  return instance
    .post(`/schools/${schoolid}/classes/${classid}/screeners/assign`, { assessmentId })
    .then((response) => response.data);
}

export function getScreeningWindows(schoolid) {
  return instance
    .get(`/schools/${schoolid}/screening-windows`)
    .then((response) => response.data);
}

export function getQR(classid) {
  return instance.get(`classes/${classid}/qr`).then((response) => {
    return response.data;
  });
}

export function getLoginBypass(classid, reason) {
  return instance
    .get(`/classes/${classid}/login-bypass`, { params: { reason } })
    .then((response) => response.data);
}

// Teacher accounts

export async function getTeachersBySchoolId(schoolid) {
  try {
    const response = await instance.get(`/schools/${schoolid}/teachers/csd`);
    return response.data;
  } catch (error) {
    console.error('Error fetching teachers for school:', error);
    throw error; // Ensure error is propagated for error handling
  }
}

export function getTeacherAccountLookup(schoolid) {
  return instance
    .get(`/schools/${schoolid}/teacher-account-lookup`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error fetching teacher account lookup:", error);
      throw error;
    });
}

export function getPendingTeacherBySchoolId(schoolid) {
  return instance
    .get(`/pending-teachers/school/${schoolid}`)
    .then((response) => response.data)
    .catch((error) => {
      if (error.response && error.response.status === 404) {
        console.warn(`No pending teachers found for school ID: ${schoolid}`);
        // Optionally return an empty array or some other default response
        return [];
      } else {
        console.error("Error fetching pending teachers:", error);
        // Rethrow the error if it's something other than 404
        throw error;
      }
    });
}

export function addAdditionalColleagues(
  schoolId,
  rollnumber,
  colleagues,
  teacherName,
  countryId
) {
  
  
  
  
  

  if (!colleagues || colleagues.length === 0) {
    console.warn("No colleagues provided to add.");
    return Promise.reject("No colleagues provided to add.");
  }

  // Include sharedClassId if provided, otherwise omit it
  const users = colleagues.map(
    ({ firstName, lastName, email, roles, sharedClassId }) => {
      const user = {
        firstName,
        lastName,
        email,
        rollnumber,
        roles: roles || "Teacher",
      };

      // Only include sharedClassId if it's provided and not null/undefined
      if (sharedClassId) {
        user.sharedClassId = sharedClassId;
      }

      return user;
    }
  );

  const payload = {
    users,
    schoolId,
    teacherName,
    countryId
  };

  

  return instance
    .post("/pending-teachers", payload)
    .then((response) => {
      
      return response.data || true; // Handle 204 responses
    })
    .catch((error) => {
      console.error("Error adding additional colleagues:", error);
      throw error;
    });
}

export function deletePendingTeacher(pendingteacherid) {
  return instance
    .delete(`/pending-teachers/${pendingteacherid}`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error deleting pending teacher:", error);
      throw error;
    });
}

export async function softDeleteTeacher(schoolid, teacherid) {
  try {
    
    
    

    const response = await instance.delete(`/schools/${schoolid}/teachers/${teacherid}/csd`);

    
    
    

    return response;
  } catch (error) {
    console.error('❌ [softDeleteTeacher] Error occurred');
    console.error('❌ [softDeleteTeacher] Error message:', error.message);
    console.error('❌ [softDeleteTeacher] Error status:', error.response?.status);
    console.error('❌ [softDeleteTeacher] Error data:', error.response?.data);
    console.error('❌ [softDeleteTeacher] Error headers:', error.response?.headers);
    console.error('❌ [softDeleteTeacher] Full error:', error);
    throw error;
  }
}

export async function deleteTeacherArchived(schoolid, data) {
  try {
    
    
    
    





    
    









    const response = await instance.delete(`/schools/${schoolid}/teachers/${data.teacherid}`, {
      data: {
        deletionReason: data.deletionReason,
        deletedByTeacherId: data.deletedByTeacherId,
        deletedByTeacherName: data.deletedByTeacherName
      }
    });

    
    
    

    return response;
  } catch (error) {
    console.error('❌ [deleteTeacherArchived] Error occurred');
    console.error('❌ [deleteTeacherArchived] Error message:', error.message);
    console.error('❌ [deleteTeacherArchived] Error status:', error.response?.status);
    console.error('❌ [deleteTeacherArchived] Error data:', error.response?.data);
    console.error('❌ [deleteTeacherArchived] Error headers:', error.response?.headers);
    console.error('❌ [deleteTeacherArchived] Error config:', error.config);
    console.error('❌ [deleteTeacherArchived] Full error:', error);
    throw error;
  }
}

export function requestPasswordReset(email) {
  return instance.post(`/user/password/request`, { email }).then((response) => {
    return response.data;
  });
}

export function resendTeacherSetupLink(schoolid, pendingteacherid) {
  return instance
    .post(`/schools/${schoolid}/teacher-account-links/setup`, { pendingteacherid })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error resending teacher setup link:", error);
      throw error;
    });
}

export function resendTeacherInvite(teacherId) {
  return instance
    .post(`/pending-teachers/remind/${teacherId}`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error resending invite:", error);
      throw error;
    });
}

export function updateTeacherProfileWithEmail(schoolId, teacherId, data) {
  return instance
    .patch(`/schools/${schoolId}/teachers/${teacherId}/profile`, data)
    .then((response) => {
      return response.data;
    });
}

export function getCurrentAlpacaLead(schoolid) {
  return instance
    .get(`/schools/${schoolid}/alpaca-lead`)
    .then((response) => {
      return response.data;
    });
}

export function addAlpacaLeadRole(schoolId, teacherId) {
  return instance
    .patch(`/schools/${schoolId}/teachers/${teacherId}/add-alpaca-lead`)
    .then((response) => response.data)
    .catch((error) => {
      console.error("Error adding Alpaca Lead role:", error);
      throw error;
    });
}

export function removeAlpacaLeadRole(schoolId, teacherId) {
  return instance
    .patch(`/schools/${schoolId}/teachers/${teacherId}/remove-alpaca-lead`)
    .then((response) => response.data)
    .catch((error) => {
      console.error("Error removing Alpaca Lead role:", error);
      throw error;
    });
}

// Support, trials, exports, and tags

export function createTeacherSupportRequest(payload) {
  return instance
    .post("/teacher-support-requests", payload)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error creating teacher support request:", error);
      throw error;
    });
}

export function requestDataIssue(schoolid, payload) {
  return instance
    .post(`/schools/${schoolid}/data-issues/requests`, payload)
    .then((response) => response.data);
}

export function requestPlatformAccess(schoolid, payload) {
  return instance
    .post(`/schools/${schoolid}/platform-access/requests`, payload)
    .then((response) => response.data);
}

export function requestTeacherWhatsappInvite(payload) {
  return instance
    .post("/teacher-support/whatsapp-invites", payload)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error requesting teacher WhatsApp invite:", error);
      throw error;
    });
}

export function getTeacherGuidanceResources({ query, type, countryId, limit } = {}) {
  const params = {};

  if (query) params.query = query;
  if (type) params.type = type;
  if (countryId) params.countryId = countryId;
  if (limit) params.limit = limit;

  return instance
    .get("/teacher-guidance-resources", { params })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error fetching teacher guidance resources:", error);
      throw error;
    });
}

export function sendTeacherGuidanceResourceLink(resourceId) {
  return instance
    .post(`/teacher-guidance-resources/${resourceId}/send-link`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error sending teacher guidance resource link:", error);
      throw error;
    });
}

export function getTrialBySchoolId(schoolid) {
  return instance.get(`/trialists/school/${schoolid}`).then((response) => {
    return response.data;
  });
}

export function getActiveTrialInstancesBySchool(schoolId) {
  return instance
    .get(`/trial-instances/active/school/${schoolId}`)
    .then((response) => response.data);
}

export function getAladdinExportReadiness(schoolid, classid, assessmentId) {
  return instance
    .get(`/schools/${schoolid}/classes/${classid}/aladdin-export/readiness`, {
      params: assessmentId ? { assessmentId } : {}
    })
    .then((response) => response.data);
}

export function getClassesByAssessmentId(
  assessmentId,
  schoolId,
  timeframe = null
) {
  let url = `/schools/${schoolId}/classes/by-assessment/${assessmentId}`;

  // Add timeframe as query parameters if provided
  if (timeframe) {
    url += `?start=${timeframe.start}&end=${timeframe.end}`;
  }

  return instance
    .get(url)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error fetching classes for assessment:", error);
      throw error;
    });
}

export function getClassesAssessmentResults(classIds, assessmentId) {
  return instance
    .post("/classes/assessments/results/v4", {
      classIds: classIds,
      assessmentId: assessmentId,
    })
    .then((response) => {
      
      return response.data;
    })
    .catch((error) => {
      // Handle any errors here
      console.error("Error fetching class assessment results:", error);
      throw error; // or return an error message, depending on your error handling strategy
    });
}

export function postAladdinExportAuditLog(
  teacherid,
  schoolId,
  assessmentName,
  assessmentId,
  classids,
  classNames,
  exportType
) {
  return instance
    .post("/aladdinresultsexportauditlogs", {
      teacherid: teacherid,
      schoolId: schoolId,
      assessmentName: assessmentName,
      assessmentId: assessmentId,
      classids: classids,
      classNames: classNames,
      exportType: exportType,
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      // Handle any errors here
      console.error("Error fetching student results:", error);
      throw error; // or return an error message, depending on your error handling strategy
    });
}

export function requestAladdinExportNotify(schoolid, classid, payload) {
  return instance
    .post(`/schools/${schoolid}/classes/${classid}/aladdin-export/notify`, payload)
    .then((response) => response.data);
}

export function sendAladdinResultsEmail(teacherEmail, teacherName, emailPayload) {
  

  return instance
    .post("/send-aladdin-results-email", {
      teacherEmail,
      teacherName,
      ...emailPayload, // Spread the already prepared payload (processed data)
    })
    .then((response) => {
      
      return response.data;
    })
    .catch((error) => {
      console.error("❌ Error sending email:", error);
      throw error;
    });
}

export function getTagDefinitions() {
  return instance
    .get("/admin/features")
    .then((response) => {
      return response.data;
    });
}

export function getEntityTags(elementType, elementId) {
  return instance
    .get(`/admin/${elementType}/${elementId}/access`)
    .then((response) => {
      return response.data;
    });
}

export function getScreenerAssignmentOptions(schoolid, classid) {
  return instance
    .get(`/schools/${schoolid}/classes/${classid}/screeners/assignment-options`)
    .then((response) => response.data);
}

export function getResolvedTags(elementType, tagName, elementIds) {
  return instance
    .get("/admin/tags/resolved", {
      params: {
        elementType,
        tagName,
        elementIds: elementIds.join(","),
      },
    })
    .then((response) => response.data);
}
